import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './CommunityEventCard.css';
import { getEventTypeInfo, formatAttendance, formatDate, formatTime } from './CommunityEventsHelpers';
import { parseUTCDate, isEventPast } from './utils/dateUtils';
import { communityEventsAPI, eventsAPI } from './services/api';
import ParticipantsDialog from './ParticipantsDialog';
import DescriptionOrg from './DescriptionOrg';

function CommunityEventCard({
  event,
  currentUser,
  showVoting = true,
  followedEvents,
  onFollowToggle,
  onVote,
  onJoinEvent,
  onLeaveEvent,
  onCreateGathering,
  onCommentVote,
  onOpenGallery,
  onOrganizerClick,
  hideParticipantsLink = false,
}) {
  const { t, i18n } = useTranslation();
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [showParticipantsDialog, setShowParticipantsDialog] = useState(false);
  const [gatheringData, setGatheringData] = useState(null);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const isOrganizer = currentUser && event.organizer && event.organizer.id === currentUser.id;
  const gatheringId = event.autoCreatedEventId || event.anyGatheringId;
  const gatheringDateTime = event.autoCreatedEventId ? event.autoCreatedEventDateTime : (event.anyGatheringDateTime || event.dateTime);
  const gatheringEnded = !event.autoCreatedEventId && !!event.anyGatheringId;
  const isPast = event.status === 1 && event.hasGatherings && isEventPast(gatheringDateTime, gatheringEnded);

  const handleShowParticipants = async () => {
    if (gatheringData) {
      setShowParticipantsDialog(true);
      return;
    }
    if (!gatheringId) return;
    try {
      setLoadingParticipants(true);
      const fullEvent = await eventsAPI.getById(gatheringId);
      setGatheringData(fullEvent);
      setShowParticipantsDialog(true);
    } catch (error) {
      console.error('Error loading participants:', error);
    } finally {
      setLoadingParticipants(false);
    }
  };
  // Don't allow organizers to vote on their own events
  // Also don't show voting if event is already approved (status === 1)
  const canVote = showVoting && !isOrganizer && event.status !== 1;
  const eventTypeInfo = getEventTypeInfo(event);
  const attendanceText = formatAttendance(event, t);

  return (
    <div className={`community-event-card${isPast ? ' past-event' : ''}`}>
      {event.photoUrl && (
        <div className="event-photo">
          <img src={event.photoUrl} alt={event.name} />
        </div>
      )}
      <div className="event-details">
        <div className="event-header">
          <h3 className="event-name">{event.name}</h3>
          <button
            className={`follow-btn ${followedEvents.has(event.id) ? 'following' : ''}`}
            onClick={() => onFollowToggle(event.id)}
            title={followedEvents.has(event.id) ? t('communityEventCard.unfollow') : t('communityEventCard.follow')}
          >
            {followedEvents.has(event.id) ? '★' : '☆'}
            {event.followCount > 0 && <span className="follow-count">{event.followCount}</span>}
          </button>
          <a
            className="event-page-btn"
            href={`/events/${event.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title={t('communityEventCard.viewEventPage')}
          >
            ↗
          </a>
        </div>        
        {isPast && <div className="past-event-badge">{t('communityEventCard.pastEvent')}</div>}
        {event.organizer && (
          <DescriptionOrg
            creator={event.organizer}
            creatorDescription={event.description}
            organizer={event.gatheringOrganizer || null}
            organizerDescription={event.gatheringDescription || null}
            onUserClick={onOrganizerClick}
          />
        )}
        <div className="event-info">
          <p className="event-address clickable-address" onClick={() => setShowMapDialog(true)}>📍 {event.address}</p>
          {gatheringDateTime && (
            <p className="event-date">
              📅 {formatDate(gatheringDateTime, i18n.language)}{formatTime(gatheringDateTime, i18n.language) && ` ${t('gatheringsSidebar.at')} ${formatTime(gatheringDateTime, i18n.language)}`}
            </p>
          )}
          {event.suitableForPeople && (
            <p className="event-capacity">{t('communityEventCard.suitableFor', { count: event.suitableForPeople })}</p>
          )}
          {attendanceText && (
            <p className="event-attendance">{attendanceText}</p>
          )}
        </div>
        <div className="event-links-row">
          {event.status === 1 && event.organizer && event.hasGatherings && gatheringId && !hideParticipantsLink && (
            <span
              className="event-secondary-link"
              onClick={handleShowParticipants}
              title={t('communityEventCard.clickToSeeParticipants')}
            >
              {loadingParticipants ? t('communityEventCard.loadingParticipants') : t('communityEventCard.participants')}
            </span>
          )}
          {onOpenGallery && (
            <span className="event-secondary-link" onClick={() => onOpenGallery(event)}>
              {t('communityEventCard.gallery')}
            </span>
          )}
          <a
            className="share-fb-btn"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/events/${event.id}`)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('communityEventCard.shareOnFacebook')}
          </a>
        </div>

        {/* Dangerous Comment Section - shows if there's an active concern */}
        {showVoting && event.status !== 1 && event.dangerousComment && (
          <div className="dangerous-comment-section">
            <div className="comment-header">
              <span className="comment-label">{t('communityEventCard.safetyConcern')}</span>
            </div>
            <div className="comment-content">
              <p className="comment-text">{event.dangerousComment.message}</p>
              <p className="comment-author">
                — {event.dangerousComment.user?.displayName || event.dangerousComment.user?.username || 'Anonymous'}
              </p>
            </div>
            <div className="comment-voting">
              <button
                className={`comment-vote-btn plus ${event.currentUserCommentVote === 1 ? 'active' : ''}`}
                onClick={() => onCommentVote(event.dangerousComment.id, 1)}
                title="Agree with concern (+1)"
              >
                +
              </button>
              <span className="comment-vote-count">{event.dangerousComment.votes || 0}</span>
              <button
                className={`comment-vote-btn minus ${event.currentUserCommentVote === -1 ? 'active' : ''}`}
                onClick={() => onCommentVote(event.dangerousComment.id, -1)}
                title="Disagree with concern (-1)"
              >
                −
              </button>
            </div>
          </div>
        )}

        {/* Voting Section - shows if there's no dangerous comment */}
        {canVote && !event.dangerousComment && (
          <div className="voting-section">
            <button
              className={`vote-btn upvote ${event.currentUserVote === 1 ? 'active' : ''}`}
              onClick={() => onVote(event.id, 1)}
              title="Upvote"
            >
              👍 {event.upvoteCount}
            </button>
            <button
              className={`vote-btn downvote ${event.currentUserVote === -1 ? 'active' : ''}`}
              onClick={() => onVote(event.id, -1)}
              title="Downvote"
            >
              👎 {event.downvoteCount}
            </button>
            {/* Report button temporarily disabled
            <button
              className={`vote-btn dangerous ${event.canUserReport === false ? 'disabled' : ''}`}
              onClick={() => event.canUserReport !== false && onMarkAsDangerous(event)}
              title={event.canUserReport === false ? (event.cannotReportReason || "Cannot report at this time") : "Mark as Dangerous"}
              disabled={event.canUserReport === false}
            >
              ⚠️
            </button>
            */}
          </div>
        )}

        {/* Show vote counts for organizers without voting buttons */}
        {isOrganizer && showVoting && event.status !== 1 && !event.dangerousComment && (
          <div className="voting-section">
            <div className="vote-count-display">
              👍 {event.upvoteCount}
            </div>
            <div className="vote-count-display">
              👎 {event.downvoteCount}
            </div>
          </div>
        )}

        {event.status === 1 && !isPast && (
          <div className="event-actions">
            {event.hasGatherings && event.autoCreatedEventId ? (
              <button
                className={`join-event-btn ${event.userHasJoinedAutoCreatedEvent ? 'disabled' : ''}`}
                onClick={() => !event.userHasJoinedAutoCreatedEvent && onJoinEvent(event.autoCreatedEventId)}
                disabled={event.userHasJoinedAutoCreatedEvent}
                title={event.userHasJoinedAutoCreatedEvent ? t('communityEventCard.alreadyJoinedTitle') : t('communityEventCard.joinEventTitle')}
              >
                {event.userHasJoinedAutoCreatedEvent ? t('communityEventCard.alreadyJoined') : t('communityEventCard.joinEvent')}
              </button>
            ) : !event.hasGatherings ? (
              <button
                className="create-gathering-btn"
                onClick={() => onCreateGathering(event)}
                title={t('communityEventCard.createGatheringTitle')}
              >
                {t('communityEventCard.createGathering')}
              </button>
            ) : null}
          </div>
        )}
      </div>

      {showParticipantsDialog && gatheringData && (
        <ParticipantsDialog
          event={gatheringData}
          title={t('communityEventCard.participantsTitle', { name: event.name })}
          currentUser={currentUser}
          isPast={isPast}
          onClose={() => setShowParticipantsDialog(false)}
          onUserClick={onOrganizerClick}
        />
      )}

      {showMapDialog && createPortal(
        <div className="map-dialog-overlay" onClick={() => setShowMapDialog(false)}>
          <div className="map-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="map-dialog-header">
              <span>📍 {event.address}</span>
              <button className="map-dialog-close" onClick={() => setShowMapDialog(false)}>×</button>
            </div>
            <iframe
              title="Event Location"
              className="map-dialog-iframe"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(event.address)}&output=embed`}
              allowFullScreen
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default CommunityEventCard;
